import React, { useState, useEffect } from 'react';
import { FlowingLines } from '../ui/FlowingLines';
import { ModernButton } from '../ui/ModernButton';
import { ThemeToggle } from '../ui/ThemeToggle';
import pb from '../../pocketbaseClient';

export function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(() => {
    try {
      if (sessionStorage.getItem('logoutReason') === 'expired') {
        sessionStorage.removeItem('logoutReason');
        return 'Du wurdest automatisch abgemeldet. Bitte melde dich erneut an.';
      }
    } catch {}
    return null;
  });
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {}
    return 'light';
  });

  // Sync theme with localStorage
  useEffect(() => {
    localStorage.setItem('theme', theme);
    // Apply dark mode to html element for global CSS
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Initial theme setup
  useEffect(() => {
    // Apply theme immediately on mount
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setNotice(null);

    try {
      await pb.collection('users').authWithPassword(username, password);
      
      if (pb.authStore.isValid) {
        console.log('Login successful:', pb.authStore.record);
        onLogin(pb.authStore.record);
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen py-8 relative overflow-hidden ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Dynamic Flowing Lines */}
                  <FlowingLines theme="emerald" opacity="60" />
      
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Theme toggle button */}
        <div className="flex justify-end mb-4">
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold rainbow-headline mb-4">
            Anmeldung
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300">
            Melden Sie sich an, um fortzufahren
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-8 rounded-2xl shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {notice && !error && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 px-4 py-3 rounded-lg">
                {notice}
              </div>
            )}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Benutzername
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                placeholder="Ihr Benutzername"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Passwort
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                placeholder="Ihr Passwort"
              />
            </div>

            <ModernButton
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Anmelden...' : 'Anmelden'}
            </ModernButton>
          </form>
        </div>
      </div>
    </div>
  );
}
